/**
 * ResolveDecisionPicker (T-11 sub-component of SaveScreen/SK-SAVE,
 * DS-1g-05 §5.1 [§2 row 4]) — AC-1g-21.
 *
 * Candidate list = OPEN decisions only (Skill #3 `view` collapsed read,
 * the same `Decision[]` DecisionLogView/T-18 consumes). Ordering is the
 * LOCKED `oldestFirst` primitive — the ONLY order this list is ever
 * shown in; there is no relevance/similarity re-ranking against the
 * current capture anywhere in this file (TW-1g-JUDGMENT-PREFILL family
 * — no auto-suggest/ranking). `selectedId` starts `null` (owned by the
 * caller) and this component never picks a default. Resolve requires a
 * non-empty rationale — enforced by the caller (SaveScreen) gating its
 * Save action on `rationale.trim().length > 0`, so there is one commit
 * locus, not a duplicated one here.
 */

import { byStatus, oldestFirst, type Decision } from "../slice/decision-slice";

export interface ResolveDecisionPickerProps {
  decisions: Decision[];
  selectedId: string | null;
  /** The picked decision's CAPTURE id (`Decision.id`), not its `decision_id`
   * register key. Skill #3's resolve locates the canonical by capture id
   * (`resolve.rs`: `decisions.find(|d| d.id == ri.decision_ref)`), so selecting
   * by `decision_id` produced an `UnknownCaptureId` rejection — the register key
   * is a different namespace (`R.01…`). Same identity the sibling
   * DecisionLogView rows have always keyed on. */
  onSelect: (decision_ref: string) => void;
  rationale: string;
  onRationaleChange: (text: string) => void;
}

export function ResolveDecisionPicker({
  decisions,
  selectedId,
  onSelect,
  rationale,
  onRationaleChange,
}: ResolveDecisionPickerProps) {
  const candidates = oldestFirst(byStatus(decisions, "open"));

  return (
    <div data-testid="resolve-decision-picker">
      <ul data-testid="resolve-decision-picker-list">
        {candidates.map((d) => (
          <li key={d.id}>
            <button
              type="button"
              data-testid={`resolve-decision-picker-option-${d.id}`}
              aria-pressed={d.id === selectedId}
              onClick={() => onSelect(d.id)}
            >
              {d.text}
            </button>
          </li>
        ))}
      </ul>
      <textarea
        data-testid="resolve-decision-picker-rationale"
        value={rationale}
        placeholder="Rationale (required)"
        onInput={(e) => onRationaleChange((e.target as HTMLTextAreaElement).value)}
      />
    </div>
  );
}
